import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./form-side-drawer.css";

/**
 * Right-side form drawer (portal + backdrop).
 * Matches the slide-over pattern used for entity add/update flows.
 */
const FormSideDrawer = ({
  isOpen = false,
  onClose,
  title,
  subtitle = "",
  icon = "ri-edit-box-line",
  children,
  footer = null,
  width = "md", // sm | md | lg
  formId = null,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  const ui = (
    <>
      <div
        className={`xc-form-drawer-backdrop ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside
        className={`xc-form-drawer xc-form-drawer--${width} ${
          isOpen ? "is-open" : ""
        }`}
        role="dialog"
        aria-modal={isOpen ? "true" : undefined}
        aria-labelledby="xc-form-drawer-title"
        aria-hidden={!isOpen}
        tabIndex={-1}
      >
        <header className="xc-form-drawer__header">
          <div className="xc-form-drawer__heading">
            <span className="xc-form-drawer__icon" aria-hidden="true">
              <i className={icon} />
            </span>
            <div>
              <h5 id="xc-form-drawer-title" className="xc-form-drawer__title">
                {title}
              </h5>
              {subtitle ? (
                <p className="xc-form-drawer__subtitle">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="xc-form-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ri-close-line" aria-hidden="true" />
          </button>
        </header>

        <div className="xc-form-drawer__body">{children}</div>

        {footer ? (
          <footer className="xc-form-drawer__footer">{footer}</footer>
        ) : null}

        {formId ? (
          <span className="visually-hidden" data-form-id={formId} />
        ) : null}
      </aside>
    </>
  );

  return createPortal(ui, document.body);
};

export default FormSideDrawer;
